import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Users, Plus, Pencil, Trash2, RefreshCw,
    X, AlertCircle, Search, ChevronDown, Check
} from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { formatTL } from '../../utils/money';
import './CourierCostsPage.css';

const CourierSearchSelect = ({ couriers, value, onChange, disabled }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (open && inputRef.current) inputRef.current.focus();
    }, [open]);

    const selected = couriers.find(c => c.id === value);
    const filtered = couriers.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="cc-search-select" ref={ref}>
            <button
                type="button"
                className={`cc-search-trigger ${open ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => { if (!disabled) setOpen(!open); }}
            >
                <span className={selected ? 'cc-search-value' : 'cc-search-placeholder'}>
                    {selected ? selected.name : 'Kurye ara ve seç...'}
                </span>
                <ChevronDown size={16} className={`cc-search-chevron ${open ? 'rotated' : ''}`} />
            </button>
            {open && (
                <div className="cc-search-dropdown">
                    <div className="cc-search-input-wrap">
                        <Search size={14} className="cc-search-input-icon" />
                        <input
                            ref={inputRef}
                            className="cc-search-input"
                            placeholder="Kurye adı ara..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="cc-search-options">
                        {filtered.length === 0 ? (
                            <div className="cc-search-empty">Sonuç bulunamadı</div>
                        ) : filtered.map(c => (
                            <button
                                key={c.id}
                                type="button"
                                className={`cc-search-option ${c.id === value ? 'selected' : ''}`}
                                onClick={() => { onChange(c.id); setOpen(false); setSearch(''); }}
                            >
                                <span>{c.name}</span>
                                {c.id === value && <Check size={14} />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const EARNING_MODES = [
    { value: 0, label: 'Taşıdığı paket kadar' },
    { value: 1, label: 'Çalıştığı saat kadar' },
    { value: 2, label: 'Günlük sabit yevmiye' },
];

const EARNING_COMBINATIONS = [
    { value: 0, label: 'Hayır, sadece mesai ücretini alsın' },
    { value: 1, label: 'Evet, ikisi de ödensin (toplanır)' },
    { value: 2, label: 'Hangisi yüksekse o ödensin' },
];

const EARNING_MODE_HINTS = {
    0: 'Kurye ne kadar paket taşırsa o kadar kazanır. Çalıştığı süre hesaba katılmaz.',
    1: 'Kurye mesaiye geldiği her saat için ücret alır. Hiç paket taşımasa da bu ücreti alır.',
    2: 'Kurye o gün işe geldiyse, kaç saat çalıştığına ve kaç paket taşıdığına bakılmadan sabit bir gündelik alır.',
};

const EARNING_COMBINATION_HINTS = {
    0: 'Kurye yalnızca mesai ücretini alır. Taşıdığı paketler için ayrıca ödeme yapılmaz.',
    1: 'Kurye önce mesai ücretini alır, üstüne taşıdığı her paketin ücreti eklenir. En yüksek maliyet bu seçenektedir.',
    2: 'Mesai ücreti ile paket kazancı karşılaştırılır, kurye yüksek olanı alır. İşin kesat olduğu günlerde kurye mağdur olmaz, yoğun günlerde de paket kazancını alır.',
};

// Paket ücretlerinin hesaba girip girmediğinin tek kaynağı (partner paneliyle birebir aynı):
// mesai/yevmiye modunda "Replace" seçilince paket ücreti hiç kullanılmaz, o yüzden zorunlu tutulmaz.
const areDeliveryFeesUsed = (earningMode, earningCombination) =>
    Number(earningMode ?? 0) === 0 || Number(earningCombination ?? 0) !== 0;

const SAMPLE_PACKAGES = 30;
// Ortalama teslimat mesafesi — km ücreti örnekte GÖRÜNSÜN diye. Km'yi örnekten dışlamak,
// filo yöneticisine gerçekte ödeyeceğinden düşük bir tutar göstermek demekti.
const SAMPLE_KM = SAMPLE_PACKAGES * 5;

const trMoney = (value) => Number(value || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Eksik veriyle yarım rakam gösterilmez: null dönerse kutu hiç render edilmez.
const buildEarningExample = (form) => {
    const mode = Number(form.earningMode ?? 0);
    const combination = Number(form.earningCombination ?? 0);
    const baseFee = Number(form.baseFee) || 0;
    const costPerKm = Number(form.costPerKm) || 0;

    // Teslimat kazancı = paket + km. Hesapta da (DeliveryCommission.CourierEarning) tek kalemdir.
    const packageEarning = baseFee * SAMPLE_PACKAGES;
    const kmEarning = costPerKm * SAMPLE_KM;
    const deliveryEarning = packageEarning + kmEarning;

    const parts = [];
    if (packageEarning > 0) parts.push(`${SAMPLE_PACKAGES} paket için ${trMoney(packageEarning)} ₺`);
    if (kmEarning > 0) parts.push(`${SAMPLE_KM} km için ${trMoney(kmEarning)} ₺`);
    const deliveryLabel = parts.join(' + ');

    if (mode === 0) {
        if (!(deliveryEarning > 0)) return null;
        return `Kurye günde ${SAMPLE_PACKAGES} paket taşıyıp ${SAMPLE_KM} km yaparsa ${deliveryLabel} = ${trMoney(deliveryEarning)} ₺ kazanır.`;
    }

    let timeEarning = 0;
    let timeLabel = '';
    if (mode === 1) {
        const hourlyRate = Number(form.hourlyRate) || 0;
        const hours = Number(form.contractedDailyHours) || 0;
        if (!(hourlyRate > 0) || !(hours > 0)) return null;
        timeEarning = hourlyRate * hours;
        timeLabel = `${hours} saat çalışınca ${trMoney(timeEarning)} ₺ mesai ücreti alır`;
    } else {
        const dailyRate = Number(form.dailyRate) || 0;
        if (!(dailyRate > 0)) return null;
        timeEarning = dailyRate;
        timeLabel = `İşe geldiği gün ${trMoney(timeEarning)} ₺ yevmiye alır`;
    }

    if (!(deliveryEarning > 0)) return `${timeLabel}.`;

    if (combination === 1) {
        return `${timeLabel}, üstüne ${SAMPLE_PACKAGES} paket taşıyıp ${SAMPLE_KM} km yaparsa ${deliveryLabel} = ${trMoney(deliveryEarning)} ₺ eklenir. Toplam ${trMoney(timeEarning + deliveryEarning)} ₺.`;
    }
    if (combination === 2) {
        const winner = deliveryEarning >= timeEarning ? 'Teslimat kazancı' : 'Mesai ücreti';
        return `${timeLabel}. ${SAMPLE_PACKAGES} paket taşıyıp ${SAMPLE_KM} km yaparsa teslimat kazancı ${trMoney(deliveryEarning)} ₺ olur; ${winner} yüksek olduğu için ${trMoney(Math.max(timeEarning, deliveryEarning))} ₺ ödenir.`;
    }
    return `${timeLabel}. ${SAMPLE_PACKAGES} paket taşıyıp ${SAMPLE_KM} km yapsa da bu tutar değişmez.`;
};

// Renk sabit kodlanmaz: filo panelinde koyu tema var ([data-theme="dark"], index.css),
// sabit gri koyu zeminde okunmuyor. Tema değişkeni iki modda da doğru kontrastı verir.
// margin YOK: grid satırlarının aralığı .form-group'un row-gap'inden gelir. Satır içi margin
// CSS'teki kuralı ezip yardım metni satırını şişiriyor ve komşu sütunla hizayı bozuyordu.
const fieldHintStyle = { margin: 0, fontSize: '12px', lineHeight: 1.4, color: 'var(--text-secondary)' };

const exampleBoxStyle = {
    margin: '0 0 16px',
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(34, 197, 94, 0.35)',
    background: 'rgba(34, 197, 94, 0.10)',
    fontSize: '13px',
    lineHeight: 1.5,
    color: 'var(--text-primary)',
};

const EMPTY_FORM = {
    courierId: '',
    baseFee: '',
    costPerKm: '',
    validFrom: new Date().toISOString().slice(0, 10),
    validTo: '',
    isActive: true,
    currency: 'TRY',
    notes: '',
    earningMode: 0,
    earningCombination: 0,
    hourlyRate: '',
    dailyRate: '',
    contractedDailyHours: '',
};

const CourierCostsPage = () => {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [couriers, setCouriers] = useState([]);
    const [filterCourier, setFilterCourier] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const formatCurrency = (amount) => {
        if (amount == null) return '-';
        return formatTL(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const fetchRecords = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.COURIER_COST_HISTORY);
            if (res.success) {
                setRecords(res.data || []);
            }
        } catch {
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchCouriers = useCallback(async () => {
        try {
            const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.MEMBERS_LIST, {
                page: 1,
                pageSize: 500,
                filterModel: { IsActive: { filter: 'true', type: 'eq' } }
            });
            if (res.success && res.data?.items) {
                setCouriers(res.data.items.map(m => ({
                    id: m.courierId,
                    name: m.courierName || m.fullName || '-'
                })));
            }
        } catch {}
    }, []);

    useEffect(() => {
        fetchRecords();
        fetchCouriers();
    }, [fetchRecords, fetchCouriers]);

    const openAdd = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditingId(record.id);
        setForm({
            courierId: record.courierId || '',
            baseFee: record.baseFee ?? '',
            costPerKm: record.costPerKm ?? '',
            validFrom: record.validFrom ? String(record.validFrom).substring(0, 10) : '',
            validTo: record.validTo ? String(record.validTo).substring(0, 10) : '',
            isActive: record.isActive,
            currency: record.currency || 'TRY',
            notes: record.notes || '',
            earningMode: record.earningMode ?? 0,
            earningCombination: record.earningCombination ?? 0,
            hourlyRate: record.hourlyRate ?? '',
            dailyRate: record.dailyRate ?? '',
            contractedDailyHours: record.contractedDailyHours ?? '',
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!form.courierId) {
            fleetToast.error('Kurye seçin.');
            return;
        }

        const earningMode = Number(form.earningMode ?? 0);
        const earningCombination = Number(form.earningCombination ?? 0);
        const deliveryFeesUsed = areDeliveryFeesUsed(earningMode, earningCombination);

        if (deliveryFeesUsed && !form.baseFee) {
            fleetToast.error('Paket ücreti doldurulmalı.');
            return;
        }
        if (!form.validFrom) {
            fleetToast.error('Başlangıç tarihi doldurulmalı.');
            return;
        }
        if (earningMode === 1 && !(Number(form.hourlyRate) > 0)) {
            fleetToast.error('Saat başına ödenecek ücreti yazın.');
            return;
        }
        if (earningMode === 1 && !(Number(form.contractedDailyHours) > 0)) {
            fleetToast.error('Kuryenin günde kaç saat çalıştığını yazın.');
            return;
        }
        if (earningMode === 2 && !(Number(form.dailyRate) > 0)) {
            fleetToast.error('Günlük yevmiye tutarını yazın.');
            return;
        }
        if (form.isActive && form.validTo && new Date(`${form.validTo}T23:59:59.999`) < new Date()) {
            fleetToast.error('Bitiş tarihi geçmişte. Bu ücretlendirme geçerli sayılmaz, kurye 0 ₺ görür.');
            return;
        }

        setSaving(true);
        try {
            // minimumDeliveryCost / maximumDeliveryCost bilerek gönderilmiyor:
            // bireysel kurye hakedişinde (CourierCostHistory) bu alanların hesapta karşılığı yok,
            // alt/üst sınır yalnızca filo tarifesinde (cost-history / CourierFleetCostHistory) çalışıyor.
            const payload = {
                courierId: form.courierId,
                baseFee: form.baseFee === '' ? 0 : parseFloat(form.baseFee),
                costPerKm: form.costPerKm === '' ? 0 : parseFloat(form.costPerKm),
                validFrom: new Date(`${form.validFrom}T00:00:00`).toISOString(),
                validTo: form.validTo ? new Date(`${form.validTo}T23:59:59.999`).toISOString() : null,
                isActive: form.isActive,
                currency: form.currency,
                notes: form.notes || null,
                earningMode,
                earningCombination,
                hourlyRate: earningMode === 1 && form.hourlyRate !== '' ? parseFloat(form.hourlyRate) : null,
                dailyRate: earningMode === 2 && form.dailyRate !== '' ? parseFloat(form.dailyRate) : null,
                contractedDailyHours: earningMode === 1 && form.contractedDailyHours !== '' ? parseFloat(form.contractedDailyHours) : null,
            };

            let res;
            if (editingId) {
                res = await apiService.put(API_ENDPOINTS.FLEET_PANEL.COURIER_COST_HISTORY_UPDATE(editingId), payload);
            } else {
                res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.COURIER_COST_HISTORY, payload);
            }

            if (res.success) {
                fleetToast.success(editingId ? 'Kayıt güncellendi' : 'Kayıt eklendi');
                closeModal();
                fetchRecords();
            } else {
                fleetToast.error(res.error || 'İşlem başarısız');
            }
        } catch {
            fleetToast.error('Bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            const res = await apiService.delete(API_ENDPOINTS.FLEET_PANEL.COURIER_COST_HISTORY_DELETE(deleteId));
            if (res.success) {
                fleetToast.success('Kayıt silindi');
                setDeleteId(null);
                fetchRecords();
            } else {
                fleetToast.error(res.error || 'Silme başarısız');
            }
        } catch {
            fleetToast.error('Bir hata oluştu');
        } finally {
            setDeleting(false);
        }
    };

    const filtered = filterCourier
        ? records.filter(r => r.courierId === filterCourier)
        : records;

    const deliveryFeesUsed = areDeliveryFeesUsed(form.earningMode, form.earningCombination);

    const groupedByCourier = {};
    filtered.forEach(r => {
        const key = r.courierId;
        if (!groupedByCourier[key]) groupedByCourier[key] = { name: r.courierName || '-', active: null, records: [] };
        groupedByCourier[key].records.push(r);
        if (r.isActive) groupedByCourier[key].active = r;
    });

    return (
        <div className="cc-page">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon"><Users size={20} /></div>
                    <div className="page-title">
                        <h1>Kurye Maliyetleri</h1>
                        <p>Kuryelerinizin teslimat maliyet tarifelerini yönetin</p>
                    </div>
                </div>
                <div className="header-right">
                    <div className="cc-header-filter">
                        <CourierSearchSelect
                            couriers={[{ id: '', name: 'Tüm Kuryeler' }, ...couriers]}
                            value={filterCourier}
                            onChange={(v) => setFilterCourier(v)}
                            disabled={false}
                        />
                    </div>
                    <button className="btn btn-secondary" onClick={fetchRecords}>
                        <RefreshCw size={16} />
                    </button>
                    <button className="btn btn-primary" onClick={openAdd}>
                        <Plus size={16} /> Yeni Tarife
                    </button>
                </div>
            </div>

            <div className="cc-table-section">
                <div className="cc-table-header">
                    <h2>Maliyet Kayıtları</h2>
                    <span className="cc-record-count">{filtered.length} kayıt</span>
                </div>

                {loading ? (
                    <div className="page-loading"><div className="spinning cc-spinner-circle"></div></div>
                ) : filtered.length === 0 ? (
                    <div className="page-empty">
                        <Users size={40} />
                        <p>Henüz kurye maliyet kaydı bulunmuyor</p>
                        <button className="btn btn-primary" onClick={openAdd}>
                            <Plus size={16} /> İlk Tarifeyi Ekle
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="data-table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Kurye</th>
                                        <th>Baz Ücret</th>
                                        <th>Km Başı</th>
                                        <th>Geçerlilik</th>
                                        <th>Durum</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((r) => (
                                        <tr key={r.id} className={r.isActive ? 'cc-row-active' : ''}>
                                            <td className="cc-courier-name">{r.courierName || '-'}</td>
                                            <td className="cc-mono">{formatCurrency(r.baseFee)}</td>
                                            <td className="cc-mono">{formatCurrency(r.costPerKm)}</td>
                                            <td>{formatDate(r.validFrom)}</td>
                                            <td>
                                                <span className={`cc-status ${r.isActive ? 'active' : 'passive'}`}>
                                                    {r.isActive ? 'Aktif' : 'Pasif'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="cc-actions">
                                                    <button className="action-btn edit" onClick={() => openEdit(r)} title="Düzenle">
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button className="action-btn delete" onClick={() => setDeleteId(r.id)} title="Sil">
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="cc-cards-mobile">
                            {filtered.map((r) => (
                                <div key={r.id} className={`cc-card ${r.isActive ? 'cc-card-active' : ''}`}>
                                    <div className="cc-card-top">
                                        <span className="cc-card-courier">{r.courierName || '-'}</span>
                                        <div className="cc-card-top-right">
                                            <span className={`cc-status ${r.isActive ? 'active' : 'passive'}`}>
                                                {r.isActive ? 'Aktif' : 'Pasif'}
                                            </span>
                                            <div className="cc-actions">
                                                <button className="action-btn edit" onClick={() => openEdit(r)}><Pencil size={14} /></button>
                                                <button className="action-btn delete" onClick={() => setDeleteId(r.id)}><Trash2 size={14} /></button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="cc-card-grid">
                                        <div><span>Baz Ücret</span><strong>{formatCurrency(r.baseFee)}</strong></div>
                                        <div><span>Km Başı</span><strong>{formatCurrency(r.costPerKm)}</strong></div>
                                    </div>
                                    <div className="cc-card-footer">{formatDate(r.validFrom)}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {modalOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingId ? 'Tarife Düzenle' : 'Yeni Tarife'}</h2>
                            <button className="modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Kurye *</label>
                                <CourierSearchSelect
                                    couriers={couriers}
                                    value={form.courierId}
                                    onChange={(v) => handleChange('courierId', v)}
                                    disabled={!!editingId}
                                />
                            </div>
                            <div className="form-group">
                                <label>Bu kuryeye nasıl ödeme yapacaksınız?</label>
                                <select
                                    value={form.earningMode}
                                    onChange={(e) => {
                                        const mode = Number(e.target.value);
                                        // Paket moduna dönünce kombinasyon anlamsızlaşır, sıfırlanmazsa
                                        // gizli alanda eski değer kalıp payload'a yanlış gidiyor.
                                        setForm(prev => ({
                                            ...prev,
                                            earningMode: mode,
                                            earningCombination: mode === 0 ? 0 : prev.earningCombination,
                                        }));
                                    }}
                                >
                                    {EARNING_MODES.map(m => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </select>
                                <p style={fieldHintStyle}>{EARNING_MODE_HINTS[Number(form.earningMode)]}</p>
                            </div>
                            {Number(form.earningMode) === 1 && (
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Saat başına ne kadar ödeyeceksiniz? (₺) *</label>
                                        <input type="number" step="0.01" min="0" value={form.hourlyRate}
                                            onChange={(e) => handleChange('hourlyRate', e.target.value)} />
                                    </div>
                                    <div className="form-group">
                                        <label>Günde kaç saat çalışıyor? *</label>
                                        <input type="number" step="0.5" min="0" value={form.contractedDailyHours}
                                            onChange={(e) => handleChange('contractedDailyHours', e.target.value)} placeholder="Örn. 9" />
                                    </div>
                                </div>
                            )}
                            {Number(form.earningMode) === 2 && (
                                <div className="form-group">
                                    <label>Bir günlük yevmiyesi ne kadar? (₺) *</label>
                                    <input type="number" step="0.01" min="0" value={form.dailyRate}
                                        onChange={(e) => handleChange('dailyRate', e.target.value)} />
                                </div>
                            )}
                            {Number(form.earningMode) !== 0 && (
                                <div className="form-group">
                                    <label>Mesai ücretinin üstüne bir de paket ücreti ödenecek mi?</label>
                                    <select
                                        value={form.earningCombination}
                                        onChange={(e) => handleChange('earningCombination', Number(e.target.value))}
                                    >
                                        {EARNING_COMBINATIONS.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                    <p style={fieldHintStyle}>{EARNING_COMBINATION_HINTS[Number(form.earningCombination)]}</p>
                                </div>
                            )}
                            {buildEarningExample(form) && (
                                <div style={exampleBoxStyle}>{buildEarningExample(form)}</div>
                            )}
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Her paket için ne kadar ödeyeceksiniz? (₺){deliveryFeesUsed ? ' *' : ''}</label>
                                    <input type="number" step="0.01" min="0" value={form.baseFee}
                                        onChange={(e) => handleChange('baseFee', e.target.value)} />
                                    <p style={fieldHintStyle}>
                                        {deliveryFeesUsed
                                            ? 'Kurye taşıdığı her paket için bu tutarı alır.'
                                            : 'Seçtiğiniz ödeme modelinde kullanılmıyor; boş bırakabilirsiniz.'}
                                    </p>
                                </div>
                                <div className="form-group">
                                    <label>Her kilometre için ne kadar? (₺)</label>
                                    <input type="number" step="0.01" min="0" value={form.costPerKm}
                                        onChange={(e) => handleChange('costPerKm', e.target.value)} />
                                    <p style={fieldHintStyle}>
                                        {deliveryFeesUsed
                                            ? 'Paket ücretine ek olarak mesafe başına ödenir. Kullanmıyorsanız 0 bırakın.'
                                            : 'Seçtiğiniz ödeme modelinde kullanılmıyor; boş bırakabilirsiniz.'}
                                    </p>
                                </div>
                            </div>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Bu ücretler ne zaman başlıyor? *</label>
                                    <input type="date" value={form.validFrom}
                                        onChange={(e) => handleChange('validFrom', e.target.value)} />
                                </div>
                                <div className="form-group">
                                    <label>Ne zamana kadar geçerli?</label>
                                    <input type="date" value={form.validTo}
                                        onChange={(e) => handleChange('validTo', e.target.value)} />
                                    <p style={fieldHintStyle}>Boş bırakırsanız süresiz geçerli olur.</p>
                                </div>
                            </div>
                            <div className="form-group cc-form-check">
                                <label>
                                    <input type="checkbox" checked={form.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)} />
                                    <span>Bu ücretler şu an geçerli olsun</span>
                                </label>
                            </div>
                            <div className="form-group">
                                <label>Kendi notunuz</label>
                                <textarea rows={3} maxLength={1000} value={form.notes}
                                    onChange={(e) => handleChange('notes', e.target.value)} placeholder="Opsiyonel not..." />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>İptal</button>
                            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                                {saving ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Kaydet')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteId && (
                <div className="modal-overlay" onClick={() => setDeleteId(null)}>
                    <div className="modal cc-modal-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Tarife Sil</h2>
                            <button className="modal-close" onClick={() => setDeleteId(null)}><X size={18} /></button>
                        </div>
                        <div className="modal-body">
                            <div className="cc-delete-warn">
                                <AlertCircle size={40} />
                                <p>Bu tarife kaydını silmek istediğinize emin misiniz?</p>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={() => setDeleteId(null)} disabled={deleting}>İptal</button>
                            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Siliniyor...' : 'Sil'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourierCostsPage;
